import type { HotelSearchFormValues } from 'frontend/features/hotels/types/hotels.presentation';
import {
  CHILDREN_AGES_PARAM,
  formatChildrenAgesParam,
  parseChildrenAgesParam,
} from 'frontend/features/hotels/utils/childrenAgesParam';

export const EMPTY_HOTEL_SEARCH: HotelSearchFormValues = {
  adults: 1,
  checkIn: '',
  checkOut: '',
  children: 0,
  childrenAges: [],
  countryCode: undefined,
  destination: '',
  destinationId: undefined,
  rooms: 1,
};

function parseCount(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

/** Reads a `/buscarxhoteles` URL back into form values. */
export function parseHotelSearchValues(search: string): HotelSearchFormValues {
  if (!search) return EMPTY_HOTEL_SEARCH;

  const params = new URLSearchParams(search);
  const children = parseCount(params.get('children'), 0);
  const ages = parseChildrenAgesParam(params.get(CHILDREN_AGES_PARAM));

  return {
    adults: parseCount(params.get('adults'), 1) || 1,
    checkIn: params.get('checkIn') ?? '',
    checkOut: params.get('checkOut') ?? '',
    children,
    // One slot per child; a URL without ages leaves them pending selection.
    childrenAges: Array.from({ length: children }, (_, i) => ages?.[i]),
    countryCode: params.get('countryCode') ?? undefined,
    destination: params.get('destination') ?? '',
    destinationId: params.get('destinationId') ?? undefined,
    rooms: parseCount(params.get('rooms'), 1) || 1,
  };
}

interface BuildHotelSearchQueryOptions {
  includeRefresh?: boolean;
  refreshToken?: number | string;
}

/**
 * Builds the `/buscarxhoteles` query string. Key set and names match what the
 * hotels results route already reads. `_refresh` is opt-in so a repeated
 * Buscar with the same stay still changes the URL and re-runs the API.
 */
export function buildHotelSearchQuery(
  values: HotelSearchFormValues,
  options: BuildHotelSearchQueryOptions = {},
): string {
  const params = new URLSearchParams();

  params.set('destination', values.destination.trim());
  if (values.destinationId) params.set('destinationId', values.destinationId);
  if (values.countryCode) params.set('countryCode', values.countryCode);
  params.set('checkIn', values.checkIn);
  params.set('checkOut', values.checkOut);
  params.set('adults', String(values.adults));
  params.set('children', String(values.children ?? 0));
  const childrenAges = formatChildrenAgesParam(values.childrenAges);
  if (childrenAges) params.set(CHILDREN_AGES_PARAM, childrenAges);
  params.set('rooms', String(values.rooms ?? 1));

  if (options.includeRefresh) {
    params.set('_refresh', String(options.refreshToken ?? Date.now()));
  }

  return `?${params.toString()}`;
}
